import {Component, OnDestroy, OnInit, ViewContainerRef} from '@angular/core';
import { ViewEncapsulation } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { HookResult, Transition, TransitionService } from "@uirouter/angular";
import {ViewService} from './services/view.service';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { ModalService } from './modals/modal.service';
import { firstValueFrom, interval, Subscription } from 'rxjs';
import { mergeMap, startWith } from 'rxjs/operators';
import { BannerService, DateHelpers, DEFAULT_PORTAL_URL_SUBDIRECTORY, EdlAlert, PortalService, SessionService, UserService } from '@edl-development/edl-common';
import { multiFactor } from 'firebase/auth';

declare let gtag: Function;
@Component({
    selector: 'edl-comply',
    templateUrl: 'edl-comply.component.html',
    encapsulation: ViewEncapsulation.None
})

export class EdlComplyComponent implements OnInit, OnDestroy{
    maintenanceNotification: Subscription;

    constructor(
        private viewService: ViewService,
        private vcr: ViewContainerRef,
        private transitionService: TransitionService,
        private modalService: ModalService,
        private loadingBarService: LoadingBarService,
        private portalService: PortalService,
        private bannerService: BannerService,
        private userService: UserService,
        public sessionService: SessionService,
        private titleService: Title,
    ) {
        viewService.viewContainerRef = vcr;

        let firebaseConfig = {
          apiKey: "AIzaSyC6Oyps1jsm38LpaAEMHiq4KapSZJZZ5ls",
          authDomain: "edl-identity.firebaseapp.com"
      };

      this.sessionService.InitializeAuth(firebaseConfig, "");

        transitionService.onSuccess({ }, (transition) => {
            try {
                gtag('set', 'page_path', window.location.pathname);
                gtag('event', 'page_view');
            } catch (e){ }

            // WCAG 2.4.2: Update page title on navigation
            this.updatePageTitle(transition);

            this.sessionService.closeMenu();
        });

        transitionService.onSuccess({ }, () => {
            if(this.sessionService.isAuthenticated() ) {
                this.sessionService.Initialized(true);
            }
            this.loadingBarService.useRef('router').complete();
        });

        transitionService.onError({}, (transition: Transition) => {
            const to = transition.to();
            const error = transition.error();
            const params = transition.params();

            this.loadingBarService.useRef('router').complete();
            // If the error occured due to the user being on the wrong portal
            // then ask the user to switch portals
            if(error?.detail?.error?.PortalID) {
                const adjustedParams = { ...params, loggedInPortalUrl: DEFAULT_PORTAL_URL_SUBDIRECTORY };
                this.modalService.wrongPortal(error.detail.error, to, adjustedParams);

            // If an error occurs due to the user not having permission to access the page
            // then take them to the unauthorized page
            } else if(error?.detail?.status === 401 || error?.detail?.status === 403) {
                transition.router.stateService.go('public.unauthorized');
            } else if ((error?.detail?.status ?? 0) >= 400) {
                transition.router.stateService.go('public.generic-error');
            }
        });

        transitionService.onBefore({}, async (transition) => {
            const from = transition.from();
            const to = transition.to();
            const isPublic = to?.data?.isPublic;


            if (!isPublic && this.sessionService.isAuthenticated() && sessionService.userAgreement && to.name !== 'public.user-agreement') {
              return transition.router.stateService.target('public.user-agreement');
            }

            else if(!isPublic && this.sessionService.mfar && (to.name !== 'authenticated.switch-portals' && to.name !== 'authenticated.users.user.edit' && to.name !== 'public.user-agreement')){
              return await this.checkAuthState(transition);
            }


            if (from.name !== to.name) {
                this.loadingBarService.useRef('router').start();
            }

        });

        // Handling switching portals through url params
        transitionService.onStart({ to: 'authenticated.**' }, this.onAuthenticatedStart);
    }

    /**
     * Handles the authenticated state transition.
     * 
     * Checks if the user is logged in and if the URL needs to be updated based on the logged in portal
     * Also handles switching the user to a different portal if needed for example, if the user is accessing a
     * link with a different portal subdirectory than their current logged in portal.
     * @param transition The transition object
     * @returns 
     */
    private onAuthenticatedStart =  (transition:Transition) => {
        const loggedInPortalUrl = transition.params()?.loggedInPortalUrl || DEFAULT_PORTAL_URL_SUBDIRECTORY;
        const currentURLSubdirectory = this.sessionService?.PortalSettings?.URLSubdirectory || DEFAULT_PORTAL_URL_SUBDIRECTORY;

        // Don't need to update the url or switch portals if:
        // the user is not logged in
        // the url already has the correct portal subdirectory
        if (!this.sessionService?.isAuthenticated() || loggedInPortalUrl == currentURLSubdirectory) {
            return true;
        }
    
        // If the user is logged into the correct portal, but the URL does not have the correct portal subdirectory
        // Updates the URL to include the correct portal subdirectory if needed
        // This occurs when a user uses a legacy link that does not include the portal subdirectory
        // It can also occur when switching the user to a different portal before knowing the correct subdirectory
        // Note: This does NOT switch the user's portal, it only updates the URL to include the correct subdirectory
        if (loggedInPortalUrl == DEFAULT_PORTAL_URL_SUBDIRECTORY) {
            const target = transition.targetState();
            const params = transition.params() ?? {};
            const updatedParams = { ...params, loggedInPortalUrl: currentURLSubdirectory };
    
            return target.withParams(updatedParams);
        }

        // Otherwise, the user needs to be switched to a different portal based on the loggedInPortalUrl param
        return this.modalService.confirm('Switch Portals', 'You are being switched to a different portal. Do you want to continue?')
            .then(() => {
                return firstValueFrom(this.userService.switchPortalByUrlSubdirectory(loggedInPortalUrl));
            }).catch(() => {
                return transition.router.stateService.target('authenticated.dashboard', { loggedInPortalUrl: currentURLSubdirectory });
            });
    }


    async checkAuthState(transition:Transition){
      return await  new Promise<HookResult>((resolve, reject) => {
        this.sessionService.GetAuthApp().onAuthStateChanged(authUser => {
          const loggedInPortal = this.sessionService.PortalSettings?.URLSubdirectory;
          const user = this.sessionService.user;
          try{
            var AuthUser = this.sessionService.GetAuthApp().currentUser!;
            const multiFactorUser = multiFactor(AuthUser);
            if(multiFactorUser.enrolledFactors.length === 0 || !user?.SQPresent){
              resolve( transition.router.stateService.target('authenticated.users.user.edit', { loggedInPortalUrl: loggedInPortal || DEFAULT_PORTAL_URL_SUBDIRECTORY, loggedInPortal, userId: user?.UserID }));
            }
            resolve(undefined);
          } catch (e){
            resolve(undefined);
          }

        });
      });
  }

    /**
     * Updates the browser page title based on the current route
     * WCAG 2.4.2 Page Titled - Pages have titles that describe topic or purpose
     * @param transition The UI-Router transition object
     */
    private updatePageTitle(transition: Transition): void {
        try {
            const toState = transition.to();

            // Try to get explicit pageTitle from state data
            let pageTitle = toState?.data?.pageTitle;

            // If no explicit title, generate from state name
            if (!pageTitle) {
                pageTitle = this.generateTitleFromStateName(toState?.name || '');
            }

            // Update the browser title
            this.titleService.setTitle(pageTitle);
        } catch (e) {
            // Fail silently - don't break navigation if title update fails
            console.warn('Failed to update page title:', e);
        }
    }

    /**
     * Generates a human-readable page title from a UI-Router state name
     * @param stateName The state name (e.g., 'authenticated.users.view')
     * @returns Formatted title (e.g., 'Users - Edulink Comply')
     */
    private generateTitleFromStateName(stateName: string): string {
        // Default fallback
        const defaultTitle = 'Edulink Comply';

        if (!stateName) {
            return defaultTitle;
        }

        // State name patterns and their titles
        const titleMap: { [pattern: string]: string } = {
            'public.login': 'Login',
            'public.forgot-password': 'Forgot Password',
            'public.user-agreement': 'User Agreement',
            'public.page-not-found': 'Page Not Found',
            'public.unauthorized': 'Unauthorized',
            'public.generic-error': 'Error',
            'authenticated.dashboard': 'Dashboard',
            'authenticated.my-items': 'My Compliance Items',
            'authenticated.users': 'User Management',
            'authenticated.groups': 'Groups',
            'authenticated.volunteerAreas': 'Volunteer Areas',
            'authenticated.requests': 'Requests',
            'authenticated.reports': 'Reports',
            'authenticated.messages': 'Messages',
            'authenticated.administration': 'Administration',
            'authenticated.questionnaires': 'Questionnaires',
            'authenticated.item-packets': 'Item Packets',
            'authenticated.supervision-management': 'Supervision Management',
            'authenticated.all-users-progress': 'All Users Progress',
            'authenticated.auto-assignment': 'Auto Assignment',
            'print': 'Print View'
        };

        // Check for exact matches first
        if (titleMap[stateName]) {
            return `${titleMap[stateName]} - ${defaultTitle}`;
        }

        // Check for partial matches (state hierarchy)
        for (const pattern in titleMap) {
            if (stateName.startsWith(pattern)) {
                return `${titleMap[pattern]} - ${defaultTitle}`;
            }
        }

        // Fallback: extract meaningful part from state name
        // e.g., 'authenticated.users.view' -> 'Users'
        const parts = stateName.split('.');
        if (parts.length > 1) {
            const mainPart = parts[1]; // Get the main section after 'authenticated'/'public'
            const formatted = mainPart
                .replace(/-/g, ' ')
                .replace(/([A-Z])/g, ' $1') // Add space before capital letters
                .trim()
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
            return `${formatted} - ${defaultTitle}`;
        }

        return defaultTitle;
    }

    ngOnInit() {
        // Check for maintenance notification every 10 minutes
        this.maintenanceNotification = interval(600000)
            .pipe(
                startWith(0),
                mergeMap(() => this.portalService.getAlert())
            ).subscribe((alert: EdlAlert) => {
                const currentMessage = alert?.AlertMessage;
                const currentSeverity = alert?.AlertSeverity;
                const previousMessage = this.bannerService.getMessage();
                const previousSeverity = this.bannerService.getSeverity();

                const startDate = DateHelpers.shortDateStringToDate(alert?.StartDate);
                const endDate = DateHelpers.shortDateStringToDate(alert?.EndDate);
                const today = DateHelpers.getDateWithoutTime(new Date());

                if ((previousMessage !== currentMessage || previousSeverity !== currentSeverity) &&
                    currentMessage &&
                    alert.IsActive &&
                    DateHelpers.isDateBetween(today, startDate, endDate)){
                    this.bannerService.setMessage(currentMessage);
                    this.bannerService.setSeverity(currentSeverity);
                    this.bannerService.open();
                }
            });
    }

    ngOnDestroy() {
        this.maintenanceNotification.unsubscribe();
    }
}
